import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { CustomEvent } from "schemas/functions";
import { LANGUAGES } from "schemas/languages";

import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";

interface IProps {
  goBack: (e?: CustomEvent) => void;
  createNiche: (e?: CustomEvent) => void;
  modifiedTemplates: Template[];
  handleTemplateChange: (e: CustomEvent, idx: number) => void;
  removeFollowUp: (idx: number) => void;
  addFollowUp: () => void;
  handleGenerateTemplate: () => void;
  filteredOutreachTemplates: Template[];
  filteredFollowUps: Template[];
  templateFromEmailSettings?: number[];
  selectedFollowups: Template[];
  followupTemplates: Template[];
  loading: boolean;
  templatesError: TemplateError | null;
  nicheLocation: UserLocation | undefined;
  locationError: "city" | "location" | null;
  handleLocationChange: (userLocation: UserLocation) => void;
  selectedLanguages: string[];
  setSelectedLanguages: (languages: string[]) => void;
}

type TemplateError = {
  idx: number;
  message: string;
};

const styles = {
  locationAutocomplete: {
    border: `1px solid ${grey[400]}`,
    borderRadius: "8px",
    width: { xs: "100%", md: "80%", lg: "80%" },
    height: 44,
    py: 0,
    px: 2,
    fontFamily: "'Inter', sans-serif",
    "&:hover, &:focus": {
      outline: "none",
    },
    fontSize: 14,
    "::placeholder": {
      color: grey[500],
    },
    mb: 2,
  },
};

export default function SetTemplate({
  goBack,
  createNiche,
  modifiedTemplates,
  handleTemplateChange,
  filteredOutreachTemplates,
  filteredFollowUps,
  templateFromEmailSettings,
  selectedFollowups,
  removeFollowUp,
  addFollowUp,
  followupTemplates,
  loading,
  handleGenerateTemplate,
  templatesError,
  nicheLocation,
  locationError,
  handleLocationChange,
  selectedLanguages,
  setSelectedLanguages,
}: IProps) {
  return (
    <>
      <Box>
        <Typography variant="h6" paragraph>
          Let's set up your outreach template 🚀
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        {modifiedTemplates[0] ? (
          <OutreachSelect
            templates={modifiedTemplates}
            handleChange={handleTemplateChange}
            filteredOutreachTemplates={filteredOutreachTemplates}
            templateFromEmailSettings={templateFromEmailSettings}
            addNewOption={handleGenerateTemplate}
            templatesError={templatesError}
          />
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateTemplate}
            sx={{ height: 25, mt: 0.4, textTransform: "none" }}
            disableElevation
          >
            Generate Template
          </Button>
        )}
      </Box>
      <Box>
        <Typography variant="h6" paragraph>
          Let's set up your follow-ups
        </Typography>
      </Box>
      <Grid container sx={{ mb: 4 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
        <Grid item xs={12}>
          {selectedFollowups?.map((followup, index) => (
            <Grid container sx={{ mb: 1 }} key={index}>
              <Grid container alignItems="center" gap={1} item xs={12}>
                <Select
                  onChange={(e) => {
                    e.stopPropagation();
                    handleTemplateChange(e, index + 1);
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                  }}
                  value={followup?.id}
                  size="small"
                >
                  {filteredFollowUps?.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Typography noWrap sx={{ maxWidth: 225 }}>
                          {template.name}
                        </Typography>
                        {template.id &&
                          templateFromEmailSettings?.includes(template.id) && (
                            <Chip
                              label="Last Used"
                              size="small"
                              sx={{
                                ml: 1,
                                fontSize: 10,
                                backgroundColor: "primary.light",
                              }}
                            />
                          )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>

                {followup?.id && (
                  <TemplateEditButton
                    sx={{ mr: -0.5 }}
                    templateId={followup.id}
                  />
                )}

                <IconButton
                  sx={{ fontSize: 13, color: grey[500] }}
                  onClick={() => removeFollowUp(index + 1)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </Grid>
              {templatesError && templatesError.idx === index + 1 && (
                <Grid container item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", color: "error.main" }}
                  >
                    {templatesError.message}
                  </Typography>
                </Grid>
              )}
              <Grid container item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", fontSize: 10 }}
                  color="textSecondary"
                >
                  Sent 4 business days after previous email
                </Typography>
              </Grid>
            </Grid>
          ))}
          {followupTemplates?.length > 0 &&
            modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
              <Grid container alignItems="center">
                <Grid item xs md>
                  <Button
                    disableElevation
                    onClick={addFollowUp}
                    variant="contained"
                  >
                    + Follow Up
                  </Button>
                </Grid>
              </Grid>
            )}
          {followupTemplates.length === 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerateTemplate}
              sx={{
                height: 25,
                mt: 0.4,
                textTransform: "none",
              }}
              disableElevation
            >
              Generate Follow-up
            </Button>
          )}
        </Grid>
      </Grid>
      <Box sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Location Priority</Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ maxWidth: "80%" }}
            paragraph
          >
            Brands in this city will be prioritized if location is relevant.
            Otherwise, we will pitch more widely.
          </Typography>
        </Box>
        {!!locationError && (
          <Box sx={{ mt: -1, mb: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontStyle: "italic", color: "error.main" }}
            >
              {locationError === "city" ? (
                <>
                  Please enter a <strong>city</strong> to save this niche
                </>
              ) : (
                <>Please enter a location to save this niche</>
              )}
            </Typography>
          </Box>
        )}
        <LocationAutocomplete
          handleLocationChange={handleLocationChange}
          customStyling={styles.locationAutocomplete}
          userLocation={nicheLocation}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Language Preference</Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ maxWidth: "80%" }}
            paragraph
          >
            Only brands that communicate in these languages will be pitched.
            Leave empty to pitch in all languages.
          </Typography>
        </Box>
        <Autocomplete
          multiple
          options={LANGUAGES}
          getOptionLabel={(option) => option.name}
          value={LANGUAGES.filter((l) => selectedLanguages.includes(l.key))}
          onChange={(_, newValue) => {
            setSelectedLanguages(newValue.map((v) => v.key));
          }}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select languages..." />
          )}
          ChipProps={{ size: "small" }}
          sx={{ width: { xs: "100%", md: "80%", lg: "80%" } }}
        />
      </Box>
      <Box>
        <Typography paragraph>
          Once this niche has been added, emails for this niche will start being
          scheduled for next week.
        </Typography>
      </Box>
      <Box gap={1} display="flex" justifyContent="flex-end">
        <Button disableElevation onClick={goBack} color="secondary">
          Back
        </Button>
        <LoadingButton
          loading={loading}
          disableElevation
          onClick={createNiche}
          variant="contained"
        >
          Save
        </LoadingButton>
      </Box>
    </>
  );
}
